import React from "react";

import { createDialogEmitter } from "#app/lib/dialog-emitter";

/** The stored appearance pair, as every entity row carries it. */
type AppearanceIcon = { icon?: string | null; color?: string | null } | null;

/**
 * What the appearance sheet edits. The row travels as a snapshot so the sheet
 * can open before a lookup lands; the mount prefers the live row whenever the
 * app data holds it, so picks paint through as they save.
 */
export type AppearanceSheetTarget =
  | { kind: "workflow"; workflow: { workflowId: string; icon: AppearanceIcon } }
  | {
      kind: "team";
      team: { teamId: string; teamName: string; icon: AppearanceIcon };
    };

type AppearanceSheetActions = {
  /** Open the phone appearance sheet for one entity. */
  openAppearanceSheet: (target: AppearanceSheetTarget) => void;
  closeAppearanceSheet: () => void;
};

/**
 * The phone's colour & icon picker for an entity whose menu cannot hold a
 * drawer (a menu row unmounts with its menu). One mount at the app level,
 * opened by state from any menu row, in the stages dialog's grammar.
 */
// Wrapped, not the union itself: the emitter's payload tuple distributes over
// a union, which would leave `open` unassignable to one target parameter.
export const appearanceSheet = createDialogEmitter<{
  target: AppearanceSheetTarget;
}>("appearance_sheet");

/** Get stable open/close callbacks. Works inside menu and sheet portals. */
export function useAppearanceSheetActions(): AppearanceSheetActions {
  return React.useMemo<AppearanceSheetActions>(
    () => ({
      openAppearanceSheet: (target) => appearanceSheet.open({ target }),
      closeAppearanceSheet: appearanceSheet.close,
    }),
    [],
  );
}
